import {
  useLazyQuery,
  useMutation,
  useQuery,
  type MutationHookOptions,
  type QueryHookOptions,
} from "@apollo/client";
import { merge } from "lodash";
import { useCallback, useContext } from "react";
import { defineMessages, useIntl } from "react-intl";
import { UserContext } from "../../../components/users/ContextProvider/UserContext";
import { BLANK_SYMBOL } from "../../constants";
import { formatTags } from "../../formats/tags";
import { formatUsername } from "../../formats/users";
import {
  AcceptInviteDocument,
  GenerateInvitationLinkDocument,
  GenerateResetPasswordLinkDocument,
  MeDocument,
  ResendInviteDocument,
  ResetPasswordDocument,
  SendResetPasswordDocument,
  SignInDocument,
  SignOutDocument,
  SignUpDocument,
  UserCreateDocument,
  UserDiscardDocument,
  UserDocument,
  UserOnboardingDocument,
  UserOptionsDocument,
  UserUpdateDocument,
  UsersDocument,
  type AcceptInviteMutation,
  type AcceptInviteMutationVariables,
  type GenerateInvitationLinkMutation,
  type GenerateInvitationLinkMutationVariables,
  type GenerateResetPasswordLinkMutation,
  type GenerateResetPasswordLinkMutationVariables,
  type InputMaybe,
  type MeQuery,
  type MeQueryVariables,
  type Permissions,
  type ResendInviteMutation,
  type ResendInviteMutationVariables,
  type ResetPasswordMutation,
  type ResetPasswordMutationVariables,
  type SendResetPasswordMutation,
  type SendResetPasswordMutationVariables,
  type SignInMutation,
  type SignInMutationVariables,
  type SignOutMutation,
  type SignOutMutationVariables,
  type SignUpMutation,
  type SignUpMutationVariables,
  type UserCreateMutation,
  type UserCreateMutationVariables,
  type UserDiscardMutation,
  type UserDiscardMutationVariables,
  type UserFragment,
  type UserOnboardingMutation,
  type UserOnboardingMutationVariables,
  type UserOptionsQuery,
  type UserOptionsQueryVariables,
  type UserQuery,
  type UserQueryVariables,
  type UserUpdateMutation,
  type UserUpdateMutationOptions,
  type UserUpdateMutationVariables,
  type UsersQuery,
  type UsersQueryVariables,
} from "../../graphql";

const messages = defineMessages({
  tenantAdmin: {
    id: "roles.tenantAdmin",
    defaultMessage: "Tenant Administrator",
  },
});

export function useCurrentUser() {
  return useContext(UserContext);
}

export function useFormatRoleTags() {
  const intl = useIntl();
  const { currentTenant } = useCurrentUser();

  return (user: UserFragment) => {
    const roles = user.roles.map((role) => role.name);

    if (currentTenant.owner?.id === user.id) {
      roles.unshift(intl.formatMessage(messages.tenantAdmin));
    }

    if (!roles.length) return BLANK_SYMBOL;

    return formatTags(roles, "green");
  };
}

export function useFormatLocalityTags() {
  const { currentTenant } = useCurrentUser();

  return (user: UserFragment) => {
    const tenantOwner = user.id === currentTenant.owner?.id;
    const localities = tenantOwner ? currentTenant.localities : user.localities;

    return formatTags(
      localities.map((locality) => locality.name),
      "blue"
    );
  };
}

export function usePermissions<T>(selector: (permissions: Permissions) => T) {
  const { user } = useCurrentUser();
  const permissions = (user && user.permissions) || {};
  return selector(permissions);
}

export function useUserPermissions(): {
  showWage?: InputMaybe<boolean>;
  showCost?: InputMaybe<boolean>;
} {
  const showWage = usePermissions((p) => p.settings?.showWage);
  const showCost = usePermissions((p) => p.settings?.showCost);

  return { showWage, showCost };
}

export function useUserOptions(
  queryOptions?: QueryHookOptions<UserOptionsQuery, UserOptionsQueryVariables>
) {
  const [load, { loading, data }] = useLazyQuery(
    UserOptionsDocument,
    queryOptions
  );

  const search = (val?: string) => {
    load(
      merge(
        {
          variables: { filter: { fullName: val } },
        },
        queryOptions
      )
    );
  };

  const options = data?.users?.items?.map((u) => ({
    key: u.id,
    email: u.email,
    label: formatUsername(u),
    user: u,
  }));

  return { load, search, loading, options };
}

export function useMe(options?: QueryHookOptions<MeQuery, MeQueryVariables>) {
  const { data, loading, error, refetch } = useQuery<MeQuery, MeQueryVariables>(
    MeDocument,
    options
  );
  return { me: data?.me, loading, error, refetch };
}

export function useUsers(
  options?: QueryHookOptions<UsersQuery, UsersQueryVariables>
) {
  const { loading, data } = useQuery(UsersDocument, options);

  if (!data || !data.users || !data.users.items) {
    return { loading };
  }

  const items = data.users.items;
  const totalCount = data.users.totalCount;

  return { loading, items, totalCount };
}

export function useUser(id: string) {
  const { data } = useQuery<UserQuery, UserQueryVariables>(UserDocument, {
    variables: { id },
    skip: !id || id === "",
  });

  return data && data.user;
}

export function useUserUpdate(
  options?: MutationHookOptions<UserUpdateMutation, UserUpdateMutationVariables>
) {
  return useMutation(UserUpdateDocument, options);
}

export function useUserCreate(
  options?: MutationHookOptions<UserCreateMutation, UserCreateMutationVariables>
) {
  return useMutation(UserCreateDocument, options);
}

export function useSignInUser(
  options?: MutationHookOptions<SignInMutation, SignInMutationVariables>
) {
  return useMutation(SignInDocument, options);
}

export function useSignUpUser(
  options?: MutationHookOptions<SignUpMutation, SignUpMutationVariables>
) {
  return useMutation(SignUpDocument, options);
}

export function useSignOutUser(
  options?: MutationHookOptions<SignOutMutation, SignOutMutationVariables>
) {
  return useMutation(SignOutDocument, options);
}

export function useResetPassword(
  options?: MutationHookOptions<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >
) {
  return useMutation(ResetPasswordDocument, options);
}

export function useSendResetPassword(
  options?: MutationHookOptions<
    SendResetPasswordMutation,
    SendResetPasswordMutationVariables
  >
) {
  return useMutation(SendResetPasswordDocument, options);
}

export function useUserDiscard(
  options?: MutationHookOptions<
    UserDiscardMutation,
    UserDiscardMutationVariables
  >
) {
  return useMutation(UserDiscardDocument, options);
}

export function useAcceptInvite(
  options?: MutationHookOptions<
    AcceptInviteMutation,
    AcceptInviteMutationVariables
  >
) {
  return useMutation(AcceptInviteDocument, options);
}

export function useResendInvite(
  options?: MutationHookOptions<
    ResendInviteMutation,
    ResendInviteMutationVariables
  >
) {
  return useMutation(ResendInviteDocument, options);
}

export function useGenerateInvitationLink(
  options?: MutationHookOptions<
    GenerateInvitationLinkMutation,
    GenerateInvitationLinkMutationVariables
  >
) {
  return useMutation(GenerateInvitationLinkDocument, options);
}

export function useGenerateResetPasswordLink(
  options?: MutationHookOptions<
    GenerateResetPasswordLinkMutation,
    GenerateResetPasswordLinkMutationVariables
  >
) {
  return useMutation(GenerateResetPasswordLinkDocument, options);
}

export function useUserSettings() {
  const { user } = useCurrentUser();
  const [update] = useUserUpdate();

  const updateSettings = useCallback(
    (settings: any, props?: UserUpdateMutationOptions) => {
      update({
        variables: {
          id: user?.id || "",
          changes: { settings: merge({}, user?.settings, settings) },
        },
        ...props,
      });
    },
    [update, user?.id, user?.settings]
  );

  const tourShown = useCallback(
    (key?: string) => {
      if (
        !key ||
        localStorage?.getItem(key) ||
        user?.impersonation?.isImpersonating
      )
        return true;

      return (
        // `shown` is backfilled for old users
        user?.settings?.productTour?.shown ||
        (key && user?.settings?.productTour?.[key])
      );
    },
    [user?.settings?.productTour, user?.impersonation?.isImpersonating]
  );

  const markProductTourStepCompleted = useCallback(
    (key?: string | null, props?: UserUpdateMutationOptions) => {
      if (!key || tourShown(key)) return;

      localStorage && localStorage.setItem(key, "true");

      updateSettings({ productTour: { [key]: true } }, props);
    },
    [updateSettings, tourShown]
  );

  return {
    settings: user?.settings,
    tourShown,
    update: updateSettings,
    markProductTourStepCompleted,
  };
}

export function useUserOnboarding(
  options?: MutationHookOptions<
    UserOnboardingMutation,
    UserOnboardingMutationVariables
  >
) {
  return useMutation(UserOnboardingDocument, options);
}
